import React from 'react';
import { apiFetch } from '../../lib/api';
import {
  TICKET_TIPO_LABEL, TICKET_PRIORITA_LABEL, TicketTipo, TicketPriorita,
} from '../../pages/assistenza/ticketAssistenza';

// Campi del ticket, condivisi fra la modale classica e la proposta
// dell'assistente. Componente controllato: lo stato sta nel padre.

export interface FormTicketValori {
  tipo: TicketTipo;
  priorita: TicketPriorita;
  titolo: string;
  descrizione: string;
  modulo: string;
  urlPagina: string;
  files: File[];
}

export const formTicketIniziale: FormTicketValori = {
  tipo: 'SUPPORTO',
  priorita: 'MEDIA',
  titolo: '',
  descrizione: '',
  modulo: '',
  urlPagina: '',
  files: [],
};

export function validaFormTicket(v: FormTicketValori): string {
  if (!v.titolo.trim()) return 'Il titolo è obbligatorio';
  if (!v.descrizione.trim()) return 'La descrizione è obbligatoria';
  return '';
}

export async function inviaTicket(v: FormTicketValori, societaId: number | null, chatId?: number | null): Promise<number> {
  const fd = new FormData();
  fd.append('tipo', v.tipo);
  fd.append('priorita', v.priorita);
  fd.append('titolo', v.titolo.trim());
  fd.append('descrizione', v.descrizione.trim());
  if (v.modulo.trim()) fd.append('modulo', v.modulo.trim());
  if (v.urlPagina.trim()) fd.append('urlPagina', v.urlPagina.trim());
  if (societaId) fd.append('societaId', String(societaId));
  if (chatId) fd.append('chatId', String(chatId));
  v.files.forEach(f => fd.append('allegati', f));
  const res = await apiFetch<{ success: boolean; data: { id: number } }>('/api/assistenza/ticket', { method: 'POST', body: fd });
  return res.data.id;
}

interface Props {
  valori: FormTicketValori;
  onChange: (v: FormTicketValori) => void;
  autoFocus?: boolean;
}

/** Immagini negli appunti (Ctrl+V di uno screenshot), rinominate con un progressivo. */
export function immaginiDaIncolla(e: React.ClipboardEvent, giaPresenti: number): File[] {
  const out: File[] = [];
  const items = e.clipboardData?.items ? Array.from(e.clipboardData.items) : [];
  for (const item of items) {
    if (item.kind !== 'file' || !item.type.startsWith('image/')) continue;
    const f = item.getAsFile();
    if (!f) continue;
    const ext = (item.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
    out.push(new File([f], `screenshot-${giaPresenti + out.length + 1}.${ext}`, { type: item.type }));
  }
  return out;
}

export default function FormTicket({ valori, onChange, autoFocus }: Props) {
  const set = (patch: Partial<FormTicketValori>) => onChange({ ...valori, ...patch });
  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    set({ files: [...valori.files, ...(e.target.files ? Array.from(e.target.files) : [])] });
  };
  // Screenshot incollato in qualunque campo del form → diventa un allegato
  const onPaste = (e: React.ClipboardEvent) => {
    const nuove = immaginiDaIncolla(e, valori.files.length);
    if (nuove.length === 0) return;
    e.preventDefault();
    set({ files: [...valori.files, ...nuove] });
  };
  const rimuoviFile = (i: number) => set({ files: valori.files.filter((_, k) => k !== i) });

  return (
    <div className="bop-form-ticket" onPaste={onPaste}>
      <div className="bop-form-row">
        <div className="bop-form-group">
          <label className="bop-label">Tipo *</label>
          <select className="bop-select" value={valori.tipo} onChange={e => set({ tipo: e.target.value as TicketTipo })}>
            {(Object.keys(TICKET_TIPO_LABEL) as TicketTipo[]).map(t => <option key={t} value={t}>{TICKET_TIPO_LABEL[t]}</option>)}
          </select>
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Priorità *</label>
          <select className="bop-select" value={valori.priorita} onChange={e => set({ priorita: e.target.value as TicketPriorita })}>
            {(Object.keys(TICKET_PRIORITA_LABEL) as TicketPriorita[]).map(p => <option key={p} value={p}>{TICKET_PRIORITA_LABEL[p]}</option>)}
          </select>
        </div>
      </div>

      <div className="bop-form-group">
        <label className="bop-label">Titolo *</label>
        <input
          className="bop-input"
          value={valori.titolo}
          onChange={e => set({ titolo: e.target.value })}
          placeholder="Riassumi in poche parole la richiesta"
          autoFocus={autoFocus}
        />
      </div>

      <div className="bop-form-group">
        <label className="bop-label">Descrizione *</label>
        <textarea
          className="bop-input"
          rows={7}
          value={valori.descrizione}
          onChange={e => set({ descrizione: e.target.value })}
          placeholder="Descrivi il problema o la richiesta nel dettaglio. Indica i passaggi per riprodurre un errore."
        />
      </div>

      <div className="bop-form-row">
        <div className="bop-form-group">
          <label className="bop-label">Modulo</label>
          <input
            className="bop-input"
            value={valori.modulo}
            onChange={e => set({ modulo: e.target.value })}
            placeholder="es. Ordini, Preventivi, Anagrafiche…"
          />
        </div>
        <div className="bop-form-group">
          <label className="bop-label">URL della pagina</label>
          <input
            className="bop-input"
            value={valori.urlPagina}
            onChange={e => set({ urlPagina: e.target.value })}
            placeholder="es. https://…/assistenza/richieste"
          />
        </div>
      </div>

      <div className="bop-form-group">
        <label className="bop-label">Allegati (immagini / screenshot)</label>
        <input className="bop-input" type="file" accept="image/*" multiple onChange={onPickFiles} />
        <div className="bop-form-ticket__hint">Puoi anche incollare uno screenshot con Ctrl+V in qualunque campo di questo modulo.</div>
        {valori.files.length > 0 && (
          <div className="bop-form-ticket__files">
            {valori.files.map((f, i) => (
              <span key={i} className="bop-badge bop-badge--neutral">
                {f.name}
                <button type="button" className="bop-form-ticket__rimuovi" aria-label={`Rimuovi ${f.name}`} onClick={() => rimuoviFile(i)}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
